import CartItemPromotionAlert from '@/modules/store/components/CartItemPromotionAlert';
import ImageThumbnail from '@/modules/store/components/ImageThumbnail';
import Price from '@/modules/store/components/Price';
import { CartDetails } from '@/modules/store/models/Cart';
import { formatPrice } from '@/utils/currency';
import { getTaxLabels } from '@/utils/tax';

interface OrderReviewTableProps {
  order: CartDetails;
  showVat?: boolean;
  countryCode?: string;
}

const OrderReviewTable = ({ order, showVat = true, countryCode }: OrderReviewTableProps) => {
  const taxLabels = getTaxLabels(countryCode);
  const colSpan = 5 + (showVat ? 1 : 0) - 2;

  return (
    <div className="table-container table-custom">
      {/* Desktop / Tablet Table */}
      <div className="hidden md:block table-wrapper">
        <table className="min-w-full sm:w-[700px] lg:w-full table-fixed">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-300 text-gray-500 font-medium text-sm">
              <th className="w-[50%] px-6 py-3 text-left text-sm">SKU</th>
              <th className="w-[12%] px-3 py-3 text-left">{taxLabels.priceExcluding}</th>
              {showVat && <th className="w-[11%] px-3 py-3 text-left">{taxLabels.rate}</th>}
              <th className="w-[11%] px-3 py-3 text-left">Order Quantity</th>
              <th className="w-[14%] px-3 py-3 pr-9 text-right">{taxLabels.subtotalExcluding}</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {order.items.map((item, index) => (
              <tr key={index} className="text-sm lg:text-base">
                <td className="px-1 py-1">
                  <div className="flex gap-4 items-center">
                    {item.sku.image && <ImageThumbnail image={item.sku.image} />}
                    <div>
                      {item.sku.name}

                      <div className="mt-1 space-y-1">
                        <CartItemPromotionAlert item={item} />
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-1">
                  <Price price={item.unitPrice} />
                </td>
                {showVat && (
                  <td className="px-3 py-1">{item.vatRate ? item.vatRate + '%' : '0%'}</td>
                )}
                <td className="px-3 py-1">{item.quantity}</td>
                <td className="px-3 py-1 pr-9 text-right">{formatPrice(item.lineItemPrice)}</td>
              </tr>
            ))}
          </tbody>
          <tbody className="border-t border-gray-300">
            <tr>
              <td colSpan={colSpan} className="text-right px-6 py-1 pt-6 font-semibold text-sm">
                {taxLabels.subtotalExcluding}
              </td>
              <td className="px-3 pt-6 py-1 pr-9 text-right">
                {formatPrice(order.subtotal, order.subtotal?.currencyCode)}
              </td>
            </tr>
            <tr>
              <td
                colSpan={colSpan}
                className={`text-right px-6 py-1 font-semibold text-sm ${!showVat && 'pb-6'}`}
              >
                {taxLabels.totalExcluding}
              </td>
              <td className={`px-3 py-1 pr-9 text-right ${!showVat && 'pb-6'}`}>
                {formatPrice(order.subtotal, order.subtotal?.currencyCode)}
              </td>
            </tr>
            {showVat && (
              <tr>
                <td colSpan={colSpan} className="text-right px-6 py-1 pb-6 font-semibold text-sm">
                  {taxLabels.grandTotalIncluding}
                </td>
                <td className="px-3 py-1 pr-9 pb-6 text-right">
                  {formatPrice(order.grandTotal, order.grandTotal?.currencyCode)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card Layout */}
      <div className="md:hidden space-y-4">
        {order.items.map((item, index) => (
          <div key={index} className="bg-white shadow rounded-lg p-4 border">
            <div className="flex gap-4 items-start">
              {item.sku.image && <ImageThumbnail image={item.sku.image} />}
              <div className="flex-1">
                <div className="text-base font-semibold mb-1">
                  {item.sku.name}

                  <div className="mt-1 space-y-1">
                    <CartItemPromotionAlert item={item} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 text-sm text-gray-600">
                  <div>{taxLabels.priceExcluding}:</div>
                  <div>
                    <Price price={item.unitPrice} />
                  </div>

                  {showVat && (
                    <>
                      <div>{taxLabels.rate}:</div>
                      <div>{item.vatRate ? item.vatRate + '%' : '0%'}</div>
                    </>
                  )}

                  <div>Order Quantity:</div>
                  <div>{item.quantity}</div>

                  <div>{taxLabels.subtotalExcluding}:</div>
                  <div>{formatPrice(item.lineItemPrice)}</div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Totals */}
        <div className="bg-white shadow rounded-lg p-4 border space-y-2 text-sm font-semibold text-gray-700">
          <div className="flex justify-between">
            <span>{taxLabels.subtotalExcluding}:</span>
            <span>{formatPrice(order.subtotal, order.subtotal?.currencyCode)}</span>
          </div>
          <div className="flex justify-between">
            <span>{taxLabels.totalExcluding}:</span>
            <span>{formatPrice(order.subtotal, order.subtotal?.currencyCode)}</span>
          </div>
          {showVat && (
            <div className="flex justify-between">
              <span>{taxLabels.grandTotalIncluding}:</span>
              <span>{formatPrice(order.grandTotal, order.grandTotal?.currencyCode)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderReviewTable;
